import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useGoogleCalendar } from "@/hooks/useGoogleCalendar";
import { useAssistantCalendarSettings } from "@/hooks/useAssistantCalendarSettings";
import type { CalendarSettings, WorkingHours } from "@/hooks/useAssistantCalendarSettings";
import { Calendar, Check, Loader2, X, AlertCircle, Settings, Save, Clock } from "lucide-react";
import { CalendarSelector, MultiCalendarSelector } from "./CalendarSelector";
import { WorkingHoursEditor } from "./WorkingHoursEditor";

interface GoogleCalendarCardProps {
  assistantId: string;
}

const DEFAULT_WORKING_HOURS: WorkingHours = {
  monday: { enabled: true, start: '09:00', end: '18:00' },
  tuesday: { enabled: true, start: '09:00', end: '18:00' },
  wednesday: { enabled: true, start: '09:00', end: '18:00' },
  thursday: { enabled: true, start: '09:00', end: '18:00' },
  friday: { enabled: true, start: '09:00', end: '18:00' },
  saturday: { enabled: false, start: '09:00', end: '13:00' },
  sunday: { enabled: false, start: '09:00', end: '13:00' }
};

const GoogleCalendarCard: React.FC<GoogleCalendarCardProps> = ({ assistantId }) => {
  const { status, connect, disconnect } = useGoogleCalendar(assistantId);
  const {
    settings,
    availableCalendars,
    loading: settingsLoading,
    loadingCalendars,
    error: settingsError,
    getSettings,
    saveSettings,
    listAvailableCalendars
  } = useAssistantCalendarSettings();

  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [localSettings, setLocalSettings] = useState<Partial<CalendarSettings>>({
    booking_calendar_id: undefined,
    conflict_calendar_ids: [],
    working_hours: DEFAULT_WORKING_HOURS,
    default_event_title: 'Reuniao',
    appointment_duration_minutes: 60
  });

  useEffect(() => {
    if (assistantId && status.connected) {
      getSettings(assistantId).then(s => {
        if (s) {
          setLocalSettings({
            booking_calendar_id: s.booking_calendar_id,
            conflict_calendar_ids: s.conflict_calendar_ids,
            working_hours: s.working_hours,
            default_event_title: s.default_event_title || 'Reuniao',
            appointment_duration_minutes: s.appointment_duration_minutes || 60
          });
        }
      });
      listAvailableCalendars(assistantId);
    }
  }, [assistantId, status.connected, getSettings, listAvailableCalendars]);

  const handleConnect = async () => {
    await connect();
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    const success = await disconnect();
    setDisconnecting(false);
    if (success) {
      setShowDisconnectDialog(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!assistantId) return;

    setSaving(true);
    const success = await saveSettings(assistantId, localSettings);
    setSaving(false);

    if (success) {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  const hasUnsavedChanges = settings && (
    settings.booking_calendar_id !== localSettings.booking_calendar_id ||
    JSON.stringify(settings.conflict_calendar_ids) !== JSON.stringify(localSettings.conflict_calendar_ids) ||
    JSON.stringify(settings.working_hours) !== JSON.stringify(localSettings.working_hours) ||
    settings.default_event_title !== localSettings.default_event_title ||
    settings.appointment_duration_minutes !== localSettings.appointment_duration_minutes
  );

  return (
    <>
      <div className="w-full flex flex-col gap-5 p-5 rounded-xl border border-[#E5E7EB] bg-white shadow-sm dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
        <div className="w-full gap-3 flex">
          <div className="w-[46px] h-[46px] flex items-center justify-center bg-white rounded-lg flex-shrink-0">
            <Calendar className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-[14px] text-[#0F172A] dark:text-[#FAFAFA] font-semibold">Google Calendar</p>
              {status.connected && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                  <Check className="w-3 h-3" />
                  Conectado
                </span>
              )}
            </div>
            {status.connected && status.email ? (
              <p className="text-[14px] font-normal text-[#64748B] dark:text-[#71717A]">
                Conectado como <span className="text-[#334155] dark:text-[#D4D4D8]">{status.email}</span>
              </p>
            ) : (
              <p className="text-[14px] font-normal text-[#64748B] dark:text-[#71717A]">
                Conecte seu Google Calendar para permitir que o assistente agende eventos automaticamente.
              </p>
            )}
            {(status.error || settingsError) && (
              <p className="text-[13px] text-red-400 flex items-center gap-1 mt-1">
                <AlertCircle className="w-3 h-3" />
                {status.error || settingsError}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          {status.connected ? (
            <>
              <Button
                variant="outline"
                className="min-w-[120px] border border-[#E5E7EB] text-[#334155] hover:bg-[#F1F5F9] transition-colors duration-200 dark:border-[#3F3F46] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                onClick={() => setShowSettings(!showSettings)}
                disabled={status.loading}
              >
                <Settings className="w-4 h-4 mr-2" />
                {showSettings ? "Ocultar" : "Configurar"}
              </Button>
              <Button
                variant="outline"
                className="min-w-[120px] border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                onClick={() => setShowDisconnectDialog(true)}
                disabled={status.loading}
              >
                {status.loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Desconectar"
                )}
              </Button>
            </>
          ) : (
            <Button
              className="min-w-[120px]"
              onClick={handleConnect}
              disabled={status.loading}
            >
              {status.loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Conectar"
              )}
            </Button>
          )}
        </div>

        {status.connected && showSettings && (
          <div className="border-t border-[#E5E7EB] dark:border-[#3F3F46] pt-5 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#334155] dark:text-[#D4D4D8]">
                  Titulo do Agendamento
                </label>
                <p className="text-xs text-[#64748B] dark:text-[#71717A] mb-2">
                  Nome padrao para os eventos criados (ex: Reuniao, Consulta).
                </p>
                <input
                  type="text"
                  value={localSettings.default_event_title || ''}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, default_event_title: e.target.value }))}
                  placeholder="Reuniao"
                  className="w-full h-10 px-3 py-2 text-sm rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 focus-visible:ring-[3px] disabled:opacity-50 disabled:cursor-not-allowed dark:border-[#3F3F464D] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                  disabled={settingsLoading}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#334155] dark:text-[#D4D4D8] flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Duracao (minutos)
                </label>
                <p className="text-xs text-[#64748B] dark:text-[#71717A] mb-2">
                  Tempo padrao de cada agendamento.
                </p>
                <input
                  type="number"
                  min={15}
                  max={480}
                  step={15}
                  value={localSettings.appointment_duration_minutes || 60}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, appointment_duration_minutes: parseInt(e.target.value) || 60 }))}
                  className="w-full h-10 px-3 py-2 text-sm rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 focus-visible:ring-[3px] disabled:opacity-50 disabled:cursor-not-allowed dark:border-[#3F3F464D] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                  disabled={settingsLoading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#334155] dark:text-[#D4D4D8]">
                Agenda para Agendamentos
              </label>
              <p className="text-xs text-[#64748B] dark:text-[#71717A] mb-2">
                Selecione em qual agenda o assistente criara os eventos.
              </p>
              <CalendarSelector
                calendars={availableCalendars}
                value={localSettings.booking_calendar_id || null}
                onChange={(id) => setLocalSettings(prev => ({ ...prev, booking_calendar_id: id || undefined }))}
                placeholder="Selecione a agenda principal"
                loading={loadingCalendars}
                disabled={settingsLoading}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#334155] dark:text-[#D4D4D8]">
                Agendas para Verificar Conflitos
              </label>
              <p className="text-xs text-[#64748B] dark:text-[#71717A] mb-2">
                Selecione quais agendas verificar ao buscar horarios disponiveis.
              </p>
              <MultiCalendarSelector
                calendars={availableCalendars}
                value={localSettings.conflict_calendar_ids || []}
                onChange={(ids) => setLocalSettings(prev => ({ ...prev, conflict_calendar_ids: ids }))}
                placeholder="Selecione as agendas de conflito"
                loading={loadingCalendars}
                disabled={settingsLoading}
              />
            </div>

            <WorkingHoursEditor
              value={localSettings.working_hours || DEFAULT_WORKING_HOURS}
              onChange={(hours) => setLocalSettings(prev => ({ ...prev, working_hours: hours }))}
              disabled={settingsLoading}
            />

            <div className="flex items-center justify-between pt-3 border-t border-[#E5E7EB] dark:border-[#3F3F46]">
              {saveSuccess && (
                <span className="text-sm text-emerald-400 flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  Configuracoes salvas!
                </span>
              )}
              {hasUnsavedChanges && !saveSuccess && (
                <span className="text-sm text-amber-400">
                  Alteracoes nao salvas
                </span>
              )}
              {!hasUnsavedChanges && !saveSuccess && <span />}

              <Button
                onClick={handleSaveSettings}
                disabled={saving || settingsLoading || !hasUnsavedChanges}
                className="min-w-[140px]"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Configuracoes
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <DialogContent className="bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <X className="w-5 h-5 text-red-400" />
              Desconectar Google Calendar
            </DialogTitle>
            <DialogDescription className="text-[#64748B] dark:text-[#71717A]">
              Tem certeza que deseja desconectar o Google Calendar?
              <br /><br />
              O assistente nao podera mais agendar eventos automaticamente ate que voce reconecte.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDisconnectDialog(false)}
              disabled={disconnecting}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="bg-red-600 hover:bg-red-700"
            >
              {disconnecting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Desconectando...
                </>
              ) : (
                "Desconectar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GoogleCalendarCard;
